/**
 * Pure "welcome back" greeting logic — Jason's direct spec tonight (LANE U1,
 * piece (c)): "a welcome back — let's see if there's any updates on new
 * roles, start away — for the returning [user]." Kept framework-free (no
 * React, no fetch), same house idiom as quickStartGuide.ts/onboardingStep.ts/
 * homeRouteGate.ts, so the returning-vs-fresh decision is directly
 * unit-testable.
 *
 * THE LAW THIS MODULE ENFORCES (NO INVENTION): "returning" is derived ONLY
 * from real served state the caller already has — never a fabricated visit
 * counter or a new server field. The brief's own definition: "has corpus/
 * roles, not first session: derive from existing served data, e.g.
 * roleCount>0 + quick-start dismissed." Both signals already exist in this
 * app (useHomeData's roleCount, quickStartGuide's readQuickStartDismissed) —
 * this module adds no new signal, only the decision that combines them.
 */

export interface WelcomeBackInput {
  /** GET /corpus/roles' length, or null while unresolved/unknown — same
   *  "unknown, not empty" discipline as useHomeData.ts's own roleCount field.
   *  A null NEVER reads as returning (an unresolved read is not evidence of a
   *  real corpus). */
  roleCount: number | null;
  /** The quick-start guide's own dismissed flag (quickStartGuide.ts's
   *  readQuickStartDismissed) — a user who has explicitly dismissed the
   *  walkthrough has necessarily been here before. */
  quickStartDismissed: boolean;
}

/**
 * A user is "returning" when they have a real, server-confirmed corpus AND
 * have already dismissed the quick-start guide — either signal alone is not
 * enough: a fresh user could in principle X-dismiss the guide before ever
 * building a corpus (never treated as returning, since there's no real story
 * yet to welcome them back TO), and a corpus could theoretically exist while
 * the guide is still showing (a returning user on a new device, guide not
 * yet dismissed here — still shown the guide, not yet the "returning"
 * language, until they act on it once more). Both true is the honest
 * "this is not their first session" read.
 */
export function isReturningUser(input: WelcomeBackInput): boolean {
  return (input.roleCount ?? 0) > 0 && input.quickStartDismissed;
}

export interface WelcomeBackGreeting {
  /** The small eyebrow line above the name — "Good morning." for a fresh/
   *  first-session user, "Welcome back" for a returning one. Never a
   *  time-of-day-dependent string (this app has no server-side clock signal
   *  wired into Home today) — a stable, warm constant either way. */
  eyebrow: string;
  /** The line that moves, per the brief ("Let's see if anything new came
   *  in") — only rendered for a returning user; null for a fresh one (no
   *  invented "let's check for updates" on someone who has nothing to check
   *  yet). */
  subline: string | null;
  /** Whether the CHECK NOW affordance renders — tied 1:1 to `returning`
   *  today, kept as its own field so a future rule (e.g. gate on being
   *  offline for N hours) can diverge from the greeting text without
   *  touching every call site that only cares about the button. */
  showCheckNow: boolean;
}

const FRESH_GREETING: WelcomeBackGreeting = {
  eyebrow: 'Good morning.',
  subline: null,
  showCheckNow: false,
};

const RETURNING_GREETING: WelcomeBackGreeting = {
  eyebrow: 'Welcome back',
  subline: "Let's see if anything new came in.",
  showCheckNow: true,
};

export function resolveWelcomeBackGreeting(input: WelcomeBackInput): WelcomeBackGreeting {
  return isReturningUser(input) ? RETURNING_GREETING : FRESH_GREETING;
}

/**
 * The honest "new since last visit" count for the returning-user banner —
 * a pure tally over jobs the caller already has in hand (GET /jobs' rows,
 * each carrying its own server-computed `new_since_last_visit` boolean per
 * jobs.routes.ts's newSinceLastVisitFragment). This module does NOT call
 * GET /jobs itself and does NOT introduce a server-side rollup count — the
 * server only exposes the per-job flag (see api.ts's JobRecord doc comment:
 * "Absent (not merely null) on POST /jobs/search rows"), so counting is the
 * client's honest job, over data it already fetched for another reason.
 *
 * Jobs whose `new_since_last_visit` field is ABSENT (not `false` — e.g. a
 * search-result row, which never carries this field per api.ts's own scope
 * note) are excluded from the count entirely, never coerced to counted-or-not
 * by a fabricated default.
 */
export function countNewSinceLastVisit(jobs: { new_since_last_visit?: boolean }[]): number {
  return jobs.reduce((count, job) => (job.new_since_last_visit === true ? count + 1 : count), 0);
}
